import axiosClient from "utils/axios";
import type { ApiList, ApiOptionList, ApiResponse } from "types/api.types";
import type {
  EligibilityCheckResult,
  GeneratePayrollRunRequest,
  GeneratePayrollRunResponse,
  PayPayrollItemRequest,
  PayRunRequest,
  PayrollItem,
  PayrollItemAdjustmentsPayload,
  PayrollItemPreview,
  PayrollRun,
  PayrollSlip,
} from "../types/payroll.types";

export const getPayrollRuns = async ({
  page,
  limit,
  search,
  month,
  year,
  status,
}: {
  page?: number;
  limit?: number;
  search?: string;
  month?: number;
  year?: number;
  status?: string;
} = {}) => {
  const { data } = await axiosClient.get<ApiResponse<ApiList<PayrollRun[]>>>(
    "/payroll",
    {
      params: {
        page,
        limit,
        search,
        month: month || undefined,
        year: year || undefined,
        status: status || undefined,
      },
    },
  );
  return data;
};

export const getEligibilityCheck = async (
  month: number,
  year: number,
): Promise<ApiResponse<EligibilityCheckResult>> => {
  const { data } = await axiosClient.get<ApiResponse<EligibilityCheckResult>>(
    "/payroll/eligibility-check",
    { params: { month, year } },
  );
  return data;
};

export const generatePayrollRun = async (body: GeneratePayrollRunRequest) => {
  const { data } = await axiosClient.post<
    ApiResponse<GeneratePayrollRunResponse>
  >("/payroll/generate", body);
  return data;
};

export const getPayrollRunById = async (id: number) => {
  const { data } = await axiosClient.get<ApiResponse<PayrollRun>>(
    `/payroll/${id}`,
  );
  console.log("Payroll run data:", data);
  return data;
};

export const finalizePayrollRun = async (id: number) => {
  const { data } = await axiosClient.post<ApiResponse<PayrollRun>>(
    `/payroll/${id}/finalize`,
  );
  return data;
};

/** Bulk-pays every FINALIZED item in the run in one transaction — all-or-nothing. */
export const payPayrollRun = async (id: number, payload?: PayRunRequest) => {
  const { data } = await axiosClient.post<ApiResponse<PayrollRun>>(
    `/payroll/${id}/pay`,
    payload,
  );
  return data;
};

export const cancelPayrollRun = async (id: number, reason?: string) => {
  const payload = reason?.trim() ? { reason: reason.trim() } : undefined;
  const { data } = await axiosClient.post<ApiResponse<PayrollRun>>(
    `/payroll/${id}/cancel`,
    payload,
  );
  return data;
};

export const deletePayrollRun = async (id: number) => {
  const { data } = await axiosClient.delete<ApiResponse<null>>(
    `/payroll/${id}`,
  );
  return data;
};

export const getPayrollItemSlip = async (itemId: number) => {
  const { data } = await axiosClient.get<ApiResponse<PayrollSlip>>(
    `/payroll/items/${itemId}/slip`,
  );
  return data;
};

export const getPayrollSlips = async ({
  page,
  limit,
  month,
  year,
  employeeId,
}: {
  page?: number;
  limit?: number;
  month?: number;
  year?: number;
  employeeId?: number;
} = {}) => {
  const { data } = await axiosClient.get<ApiResponse<ApiList<PayrollSlip[]>>>(
    "/payroll/slips",
    { params: { page, limit, month, year, employeeId } },
  );
  return data;
};

export const downloadPayrollSlipPdf = async (slipId: number) => {
  const response = await axiosClient.get(`/payroll/slips/${slipId}/download`, {
    responseType: "blob",
  });
  return response.data as Blob;
};

export const getMyPayrollSlips = async ({
  page,
  limit,
  month,
  year,
}: { page?: number; limit?: number; month?: number; year?: number } = {}) => {
  const { data } = await axiosClient.get<ApiResponse<ApiList<PayrollSlip[]>>>(
    "/payroll/me/slips",
    { params: { page, limit, month, year } },
  );
  return data;
};

export const downloadMyPayrollSlipPdf = async (slipId: number) => {
  const response = await axiosClient.get(
    `/payroll/me/slips/${slipId}/download`,
    { responseType: "blob" },
  );
  return response.data as Blob;
};

export const getPayrollItemPreview = async (itemId: number) => {
  const { data } = await axiosClient.get<ApiResponse<PayrollItemPreview>>(
    `/payroll/items/${itemId}/preview`,
  );
  return data;
};

export const updatePayrollItemAdjustments = async (
  itemId: number,
  payload: PayrollItemAdjustmentsPayload,
) => {
  const { data } = await axiosClient.put<ApiResponse<PayrollItem>>(
    `/payroll/items/${itemId}/adjustments`,
    payload,
  );
  return data;
};

/** Fallback for paying one FINALIZED item individually — modeOfPaymentId only required if netAmount > 0. */
export const payPayrollItem = async (
  itemId: number,
  payload?: PayPayrollItemRequest,
) => {
  const { data } = await axiosClient.post<ApiResponse<PayrollItem>>(
    `/payroll/items/${itemId}/pay`,
    payload,
  );
  return data;
};

/** Searchable options for a FINALIZED payroll item — used by the generic Payment form. */
export const getPayrollItemOptions = async ({
  query,
  limit,
  page,
  status,
  unpaid,
}: {
  query?: string;
  limit?: number;
  page?: number;
  status?: string;
  /** Explicit "computed, payable, not yet paid" filter — takes precedence over `status`. */
  unpaid?: boolean;
} = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `/payroll/items/options`,
      { params: { query, limit, page, status, unpaid } },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching Payroll Item Options : ", err);
    throw err;
  }
};
